import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaFilter } from "react-icons/fa";

const ReportPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('c2c-transactions');
  const [c2cTransactionsData, setC2cTransactionsData] = useState([]);
  const [referenceData, setReferenceData] = useState([]);
  const [thankYouData, setThankYouData] = useState([]);
  const [testimonialData, setTestimonialData] = useState([]);
  const [asksData, setAsksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filter panel state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    chapterId: '',
    clusterId: '',
    startDate: '',
    endDate: ''
  });

  // Dropdown options
  const [allChapters, setAllChapters] = useState([]);
  const [allClusters, setAllClusters] = useState([]);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  // Tab configuration
  const tabs = [
    { key: 'c2c-transactions', label: 'C2C'  },
    { key: 'reference', label: 'Reference'  },
    { key: 'thankyou', label: 'Thank You Slip'  },
    { key: 'testimonial', label: 'Testimonial' },
    { key: 'asks', label: 'Asks' }
  ];

  // Fetch chapters and clusters for dropdowns
    useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [chaptersRes, clustersRes] = await Promise.all([
          axios.get('/admin/api/chapters/all'),
          axios.get('/admin/api/clusters/all')
        ]);
        setAllChapters(chaptersRes.data.chapter || []);
        setAllClusters(clustersRes.data.cluster || []);
      } catch (err) {
        console.error('Failed to fetch chapters or clusters:', err);
        setAllChapters([]);
        setAllClusters([]);
      }
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    fetchData(activeTab, currentPage, searchQuery, filters);
  }, [activeTab, currentPage, searchQuery, filters]);

  // Accept filters param and use only for c2c-transactions
  const fetchData = async (tab, page = 1, search = '', filters = {}) => {
    setLoading(true);
    try {
      let endpoint = '';
      let params = {};
      switch (tab) {
        case 'c2c-transactions':
          endpoint = `/api/c2c-transactions`;
          params = {
            page,
            limit,
            givenId: search || undefined,
            receiverId: search || undefined,
            search: search || undefined,
            clusterId: filters.clusterId || undefined,
            chapterId: filters.chapterId || undefined,
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined
          };
          break;
        case 'reference':
          endpoint = `/api/references?page=${page}&limit=${limit}&search=${search}`;
          break;
        case 'thankyou':
          endpoint = `/api/thank-you-slips?page=${page}&limit=${limit}&search=${search}`;
          break;
        case 'testimonial':
          endpoint = `/api/testimonials?page=${page}&limit=${limit}&search=${search}`;
          break;
        case 'asks':
          endpoint = `/api/asks?page=${page}&limit=${limit}&search=${search}`;
          break;
        default:
          return;
      }

      let res;
      if (tab === 'c2c-transactions') {
        res = await axios.get(endpoint, { params });
      } else {
        res = await axios.get(endpoint);
      }

      let data = [];
      let totalCount = 0;

      if (tab === 'c2c-transactions') {
        data = res.data.transactions || [];
        totalCount = res.data.totalCount || 0;
      } else if (tab === 'reference') {
        data = res.data.references || [];
        totalCount = res.data.totalCount || 0;
      } else if (tab === 'thankyou') {
        data = res.data.slips || [];
        totalCount = res.data.totalCount || 0;
      } else if (tab === 'testimonial') {
        data = res.data.testimonials || [];
        totalCount = res.data.totalCount || 0;
      } else if (tab === 'asks') {
        data = res.data.asks || [];
        totalCount = res.data.totalCount || 0;
      } else {
        data = res.data.data || [];
        totalCount = res.data.totalCount || 0;
      }

      switch (tab) {
        case 'c2c-transactions':
          setC2cTransactionsData(data);
          break;
        case 'reference':
          setReferenceData(data);
          break;
        case 'thankyou':
          setThankYouData(data);
          break;
        case 'testimonial':
          setTestimonialData(data);
          break;
        case 'asks':
          setAsksData(data);
          break;
      }

      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error(`Failed to fetch ${tab} data:`, err);
      toastError(`Failed to load ${tab} data`);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setCurrentPage(1);
    setSearchQuery('');
    setShowFilters(false);
    if (tabKey === 'c2c-transactions') {
      setFilters({
        chapterId: '',
        clusterId: '',
        startDate: '',
        endDate: ''
      });
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Handle filter change for c2c-transactions
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilters({
      chapterId: '',
      clusterId: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'c2c-transactions':
        return c2cTransactionsData;
      case 'reference':
        return referenceData;
      case 'thankyou':
        return thankYouData;
      case 'testimonial':
        return testimonialData;
      case 'asks':
        return asksData;
      default:
        return [];
    }
  };

  const getInitials = (name) => {
    if (!name) return 'NA';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  // --- Table Render Functions ---
  const renderThankYouTable = () => {
    const data = getCurrentData();
    if (loading) return <div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (data.length === 0) return <div className="text-center py-5 text-muted"><h5>No thank you slips found</h5></div>;
    return (
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Giver</th>
              <th>Receiver</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {data.map((slip, idx) => {
              const givenName = `${slip.given?.firstName || ''} ${slip.given?.middleName || ''} ${slip.given?.lastName || ''}`.trim() || '';
              const receiverName = `${slip.receiver?.firstName || ''} ${slip.receiver?.middleName || ''} ${slip.receiver?.lastName || ''}`.trim() || '';
              return (
                <tr key={slip._id || idx}>
                  <td>
                    <div className="d-flex align-items-center">
                      {slip.given?.profilepic ? (
                        <img src={slip.given.profilepic} alt={givenName} className="rounded-circle me-2"  height="30" width="30" />
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(givenName)}</div>
                      )}
                      <div>{givenName}</div>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {slip.receiver?.profilepic ? (
                        <img src={slip.receiver.profilepic} alt={receiverName} className="rounded-circle me-2" height="30" width="30"/>
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(receiverName)}</div>
                      )}
                      <div>{receiverName}</div>
                    </div>
                  </td>
                  <td><div style={{ fontWeight: '500', color: '#28a745' }}>₹{slip.price || 0}</div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderReferencesTable = () => {
    const data = getCurrentData();
    if (loading) return <div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (data.length === 0) return <div className="text-center py-5 text-muted"><h5>No references found</h5></div>;
    return (
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Giver</th>
              <th>Receiver</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {data.map((reference, idx) => {
              const givenName = `${reference.given?.firstName || ''} ${reference.given?.middleName || ''} ${reference.given?.lastName || ''}`.trim() || '';
              const receiverName = `${reference.receiver?.firstName || ''} ${reference.receiver?.middleName || ''} ${reference.receiver?.lastName || ''}`.trim() || '';
              return (
                <tr key={reference._id || idx}>
                  <td>
                    <div className="d-flex align-items-center">
                      {reference.given?.profilepic ? (
                        <img src={reference.given.profilepic} alt={givenName} className="rounded-circle me-2" height="30" width="30" />
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(givenName)}</div>
                      )}
                      <div>{givenName}</div>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {reference.receiver?.profilepic ? (
                        <img src={reference.receiver.profilepic} alt={receiverName} className="rounded-circle me-2" height="30" width="30"/>
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(receiverName)}</div>
                      )}
                      <div>{receiverName}</div>
                    </div>
                  </td>
                  <td title={reference.description}>{reference.description?.length > 50 ? `${reference.description.substring(0, 50)}...` : reference.description || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderAsksTable = () => {
    const data = getCurrentData();
    if (loading) return <div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (data.length === 0) return <div className="text-center py-5 text-muted"><h5>No asks found</h5></div>;
    return (
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Given By</th>
              <th>Type</th>
              <th>Category</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {data.map((ask, idx) => {
              const givenName = ask.given ? `${ask.given?.firstName || ''} ${ask.given?.middleName || ''} ${ask.given?.lastName || ''}`.trim() : '';
              const categoryName = ask.category?.name || '';
              const askType = ask.type || '';
              return (
                <tr key={ask._id || idx}>
                  <td>
                    <div className="d-flex align-items-center">
                      {ask.given?.profilepic ? (
                        <img src={ask.given.profilepic} alt={givenName} className="rounded-circle me-2" height="30" width="30"/>
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(givenName)}</div>
                      )}
                      <div>{givenName}</div>
                    </div>
                  </td>
                  <td>
                    <span className="badge" style={{ backgroundColor: askType === 'Ask' ? '#28a745' : '#17a2b8', fontSize: '11px' }}>{askType}</span>
                  </td>
                  <td>{categoryName}</td>
                  <td title={ask.description}>{ask.description?.length > 50 ? `${ask.description.substring(0, 50)}...` : ask.description || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTestimonialsTable = () => {
    const data = getCurrentData();
    if (loading) return <div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (data.length === 0) return <div className="text-center py-5 text-muted"><h5>No testimonials found</h5></div>;
    return (
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Given</th>
              <th>Receiver</th>
            </tr>
          </thead>
          <tbody>
            {data.map((testimonial, idx) => {
              const givenName = `${testimonial.given?.firstName || ''} ${testimonial.given?.middleName || ''} ${testimonial.given?.lastName || ''}`.trim() || '';
              const receiverName = `${testimonial.receiver?.firstName || ''} ${testimonial.receiver?.middleName || ''} ${testimonial.receiver?.lastName || ''}`.trim() || '';
              return (
                <tr key={testimonial._id || idx}>
                  <td>
                    <div className="d-flex align-items-center">
                      {testimonial.given?.profilepic ? (
                        <img src={testimonial.given.profilepic} alt={givenName} className="rounded-circle me-2" height="30" width="30"/>
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2
                        custom-background text-white">{getInitials(givenName)}</div>
                      )}
                      <div>{givenName}</div>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {testimonial.receiver?.profilepic ? (
                        <img src={testimonial.receiver.profilepic} alt={receiverName} className="rounded-circle me-2" height="30" width="30"/>
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(receiverName)}</div>
                      )}
                      <div>{receiverName}</div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // C2C Transactions Table with filters
  const renderC2CTransactionsTable = () => {
    const data = getCurrentData();
    if (loading) return <div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (data.length === 0) return <div className="text-center py-5 text-muted"><h5>No C2C transactions found</h5></div>;
    return (
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Assign Name</th>
              <th>Date & Time</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {data.map((transaction, idx) => {
              const givenName = `${transaction.given?.firstName || ''} ${transaction.given?.middleName || ''} ${transaction.given?.lastName || ''}`.trim() || '';
              const receiverName = `${transaction.receiver?.firstName || ''} ${transaction.receiver?.middleName || ''} ${transaction.receiver?.lastName || ''}`.trim() || '';
              const createdDate = transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : '';
              return (
                <tr key={transaction._id || idx}>
                  <td>
                    <div className="d-flex align-items-center">
                      {transaction.given?.profilepic ? (
                        <img src={transaction.given.profilepic} alt={givenName} className="rounded-circle mr-2" height="30" width="30" />
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(givenName)}</div>
                      )}
                      <div>{givenName}</div>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {transaction.receiver?.profilepic ? (
                        <img src={transaction.receiver.profilepic} alt={receiverName} className="rounded-circle" height="30" width="30" />
                      ) : (
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2 custom-background text-white">{getInitials(receiverName)}</div>
                      )}
                      <div>{receiverName}</div>
                    </div>
                  </td>
                  <td>
                    <div>{createdDate}</div>
                    {transaction.startDate && transaction.endDate && (
                      <small className="text-muted" style={{ fontSize: '11px', lineHeight: '1.2' }}>{new Date(transaction.startDate).toLocaleDateString()} - {new Date(transaction.endDate).toLocaleDateString()}</small>
                    )}
                  </td>
                  <td title={transaction.description}>{transaction.description?.length > 50 ? `${transaction.description.substring(0, 50)}...` : transaction.description || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Filter panel for C2C
  const renderC2CFilters = () => (
    <div className="card mb-3 border-0" style={{ background: '#f6f6f6' }}>
      <div className="card-body p-3">
        <div className="row g-3 align-items-center">
          <div className="col-md-3">
            <label className="form-label mb-1" htmlFor="chapterId">Chapter</label>
           <select
              className="form-select"
              name="chapterId"
              id="chapterId"
              value={filters.chapterId}
              onChange={handleFilterChange}
            >
              <option value="">All Chapters</option>
              {Array.isArray(allChapters) && allChapters.map(chapter => (
                <option key={chapter._id} value={chapter._id}>{chapter.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1" htmlFor="clusterId">Cluster</label>
            <select
              className="form-select"
              name="clusterId"
              id="clusterId"
              value={filters.clusterId}
              onChange={handleFilterChange}
            >
              <option value="">All Clusters</option>
              {allClusters.map(cluster => (
                <option key={cluster._id} value={cluster._id}>
                  {cluster.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label mb-1" htmlFor="startDate">Start Date</label>
            <input
              type="date"
              className="form-control"
              name="startDate"
              id="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              placeholder="Start Date"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label mb-1" htmlFor="endDate">End Date</label>
            <input
              type="date"
              className="form-control"
              name="endDate"
              id="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              placeholder="End Date"
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button className="btn btn-outline-secondary me-2" type="button" onClick={handleFilterReset}>
              Reset
            </button>
            {/* <button className="btn btn-primary" type="button" onClick={() => fetchData('c2c-transactions', 1, searchQuery, filters)}>
                          Apply
                        </button> */}
          </div>
        </div>
      </div>
    </div>
  );

  // --- Page Render ---
  return (
    <div className="container-fluid mt-2">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Activity</h3>
        <div className="d-flex align-items-center w-20">
          <input
            type="text"
            className="form-control mr-2"
            placeholder={`Search ${tabs.find(t => t.key === activeTab)?.label}...`}
            value={searchQuery}
            onChange={handleSearch}
          />
          {activeTab === 'c2c-transactions' && (
            <button
              type="button"
              onClick={() => setShowFilters((prev) => !prev)}
              style={{
                border: "1px solid #C44C23",
                backgroundColor: "transparent"
              }}
              className="btn btn-sm text-primary-custom"
            >
              <FaFilter className="me-1" />
          </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="card shadow-sm">
        <div className="card-header p-0">
          <ul className="nav nav-tabs nav-fill" style={{ borderBottom: 'none' }}>
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.key}>
                <button
                  className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                  style={{
                    backgroundColor: activeTab === tab.key ? '#C44C23' : 'transparent',
                    color: activeTab === tab.key ? 'white' : '#C44C23',
                    border: 'none',
                    borderRadius: '0',
                    fontWeight: '500'
                  }}
                  onClick={() => handleTabChange(tab.key)}
                >
                  <span className="me-2">{tab.icon}</span>
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Tab Content */}
        <div className="card-body p-4">
          {/* Show filters only for c2c-transactions above table */}
          {activeTab === 'c2c-transactions' && showFilters && renderC2CFilters()}
          {activeTab === 'c2c-transactions' ? renderC2CTransactionsTable() : 
           activeTab === 'reference' ? renderReferencesTable() : 
           activeTab === 'thankyou' ? renderThankYouTable() :
           activeTab === 'testimonial' ? renderTestimonialsTable() :
           activeTab === 'asks' ? renderAsksTable() : null}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div className="text-muted">
              Page {currentPage} of {totalPages}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo; Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page = i + 1;
                if (totalPages > 5) {
                  if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                }
                return (
                  <button
                    key={page}
                    className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPage;